import React, { useState } from 'react';

import { Drawer, ListItemIcon, ListItemText, MenuItem, MenuList } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import DirectionsIcon from '@material-ui/icons/Directions';
import EditIcon from '@material-ui/icons/Edit';
import ExitToApp from '@material-ui/icons/ExitToApp';
import StarIcon from '@material-ui/icons/Star';
import DescriptionIcon from '@material-ui/icons/Description';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import CloudIcon from "@material-ui/icons/CheckCircle";
import FileCopyIcon from '@material-ui/icons/FileCopy';
import InfoIcon from '@material-ui/icons/Info';

import { LibraryItem } from '../../../entities/LibraryItem';
import { LibraryItemType } from '../../../entities/LibraryItemType';
import { useOfflineAssets } from '../../offline/useOfflineAssets';
import { useHistory } from 'react-router-dom';
import { routes } from '../../../routes';
import { FileAssetService } from '../../../services/FIleAssetService';
import { BuilderAssetService, SaveBuilderAssetRequest } from '../../../services/BuilderAssetService';
import { UserAgent } from '../../../utilities/UserAgent';
import { useLibrary, useLibraryAlert } from '../LibraryPage';
import { AssetCategory } from '../../../entities/AssetCategory';
import { RenameAssetDialog } from './RenameAssetDialog';
import { DeleteAssetDialog } from './DeleteAssetDialog';
import { UseTemplateDialog } from './UseTemplateDialog';
import { convertBuilderAssetToLibraryItem } from '../../../entities/BuilderAsset';
import { useIsMobile } from '../../../components/Mui3WidthProvider';
import { RightDrawer } from '../RightDrawer/RightDrawer';
import { useAppConfig } from '../../../utilities/useAppConfig';
import { useAuthenticatedUser } from '../../../utilities/useAuthenticatedUser';
import { MoveAssetMenu } from './MoveAsset/MoveAssetMenu';
import { convertFileAssetToLibraryItem } from '../../../entities/FileAsset';
import { convertFolderToLibraryItem } from '../../../entities/Folder';
import { convertLinkAssetToLibraryItem } from '../../../entities/LinkAsset';
import { convertSeriesToLibraryItem } from '../../../entities/SermonSeries';
import { FolderService } from '../../../services/FolderService';
import { LinkAssetService } from '../../../services/LinkAssetService';
import { SermonSeriesService } from '../../../services/SermonSeriesService';

interface Props {
    focusedItem: LibraryItem
    onClose: () => void
}

export function LibraryAssetActions(props:Props){
    const { focusedItem } = props;
    const history = useHistory();
    const context = useLibrary();
    const libraryAlert = useLibraryAlert();
    const { activeFolderId } = context;
    const isMobile = useIsMobile();
    const [config] = useAppConfig();
    const user = useAuthenticatedUser();
    const { setAssetOfflineMode } = useOfflineAssets();
    
    const [showMoveMenu, setShowMoveMenu] = useState(false);
    const [, setMoveMenuAchor] = useState<HTMLElement>();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showRenameDialog, setShowRenameDialog] = useState(false);
    const [showUseTemplateDialog, setShowUseTemplateDialog] = useState(false);
    const [showDetailDrawer, setShowDetailDrawer] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [downloading, setDownloading] = useState(false)

    const onOfflineEnabledToggle = async() => {
        setAssetOfflineMode(focusedItem.id, !focusedItem.offlineMode, "Right Click (Library)");

        setDisabled(true)
        const response = await BuilderAssetService.setOfflineMode(focusedItem.id, !focusedItem.offlineMode);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const asset = response.data;
            context.updateLibraryItem(convertBuilderAssetToLibraryItem(asset));
            libraryAlert(`${asset.name} ${asset.offlineMode ? "is available offline" : "is no longer available offline"}`)
            props.onClose();
        }
    }

    const handleStar = () => {
        switch(focusedItem.type){
            case LibraryItemType.Folder:
            case LibraryItemType.PremiumSermon:
            case LibraryItemType.PremiumSermonSeries:
                return handleStarFolder();
            case LibraryItemType.SermonSeries:
                return handleStarSeries();
            case LibraryItemType.BuilderAsset:
                return handleStarBuilderAsset();
            case LibraryItemType.FileAsset:
                return handleStarFile();
            case LibraryItemType.LinkAsset:
                return handleStarLink();
        }
    }

    const handleStarBuilderAsset = async() => {
        setDisabled(true)
        const response = await BuilderAssetService.setStarred(focusedItem.id, !focusedItem.isStarred);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const asset = response.data;
            context.updateLibraryItem(convertBuilderAssetToLibraryItem(asset));
            libraryAlert(`${asset.isStarred ? "Star added to" : "Star removed from"} ${asset.name}`)
        }
        props.onClose();
    }

    const handleStarFile = async() => {
        setDisabled(true)
        const response = await FileAssetService.setStarred(focusedItem.id, !focusedItem.isStarred);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const asset = response.data;
            context.updateLibraryItem(convertFileAssetToLibraryItem(asset));
            libraryAlert(`${asset.isStarred ? "Star added to" : "Star removed from"} ${asset.name}`)
        }
        props.onClose();
    }

    const handleStarLink = async() => {
        setDisabled(true)
        const response = await LinkAssetService.setStarred(focusedItem.id, !focusedItem.isStarred);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const asset = response.data;
            context.updateLibraryItem(convertLinkAssetToLibraryItem(asset));
            libraryAlert(`${asset.isStarred ? "Star added to" : "Star removed from"} ${asset.name}`)
        }
        props.onClose();
    }

    
    const handleStarFolder = async() => {
        setDisabled(true)
        const response = await FolderService.setStarredFolder(focusedItem.id, !focusedItem.isStarred);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const asset = response.data;
            context.updateLibraryItem(convertFolderToLibraryItem(asset));
            libraryAlert(`${asset.isStarred ? "Star added to" : "Star removed from"} ${asset.name}`)
        }
        props.onClose();
    }

    const handleStarSeries = async() => {
        setDisabled(true)
        const response = await SermonSeriesService.setStarredSeries(focusedItem.id, !focusedItem.isStarred);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const asset = response.data;
            context.updateLibraryItem(convertSeriesToLibraryItem(asset));
            libraryAlert(`${asset.isStarred ? "Star added to" : "Star removed from"} ${asset.name}`)
        }
        props.onClose();
    }

    //TODO: make this server side.
    const handleDuplicateTemplate = async() => {
        const request: SaveBuilderAssetRequest = {
            parentId: activeFolderId ?? undefined,
            name: focusedItem.name + " - Copy",
            isStarred: false,
            dateAccessed: new Date(),
            category: focusedItem.category ?? AssetCategory.Manuscript,
            text: focusedItem.templateText!,
            slideCount: 0,
            isTemplate: true,
            templateDescription: focusedItem.templateDescription,
            customSlideBackgroundImagePath: focusedItem.customSlideBackgroundImagePath,
            slideExporterAspectRatio: focusedItem.slideExporterAspectRatio,
            offlineMode: false,
            lastModifiedFormFactor: UserAgent.getFormFactor(),
            lastModifiedOnOperatingSystem: UserAgent.getOperatingSystem(),
        };

        setDisabled(true);
        const response = await BuilderAssetService.saveFromTemplate(request);
        setDisabled(false);
        
        if(response.error) return libraryAlert(response.message);
        
        if(response.success)
            history.push(routes.app.resolve.builderPage(response.data.id), { prevUrl: window.location.href });
    }

    const handleMoveClick = (event:React.MouseEvent<HTMLElement>) => {
        setShowMoveMenu(true);
        setMoveMenuAchor(event.currentTarget);
    }

    const handleMoveMenuClose = () => {
        setShowMoveMenu(false);
        setMoveMenuAchor(undefined);
    }

    const handleDownload = async() => {
        setDownloading(true);                
        await FileAssetService.downloadFile(focusedItem.id, focusedItem.name)
        setDownloading(false);       
    }
    
    return (
        <>
        { showMoveMenu
        ?  <MoveAssetMenu focusedItem={focusedItem} open={showMoveMenu} onClose={handleMoveMenuClose} />
        :
            <MenuList>
                { isMobile &&
                    <MenuItem 
                        onClick={() => setShowDetailDrawer(true) }
                        disabled={ disabled }
                    >
                        <ListItemIcon>
                            <InfoIcon />
                        </ListItemIcon>
                        <ListItemText primary="Details" />
                    </MenuItem>
                }
                <MenuItem 
                    onClick={() => setShowRenameDialog(true) }
                    disabled={ disabled }
                    >
                    <ListItemIcon>
                        <EditIcon />
                    </ListItemIcon>
                    <ListItemText primary="Rename" />
                </MenuItem>
                <MenuItem onClick={handleStar} disabled={ disabled }>
                    <ListItemIcon>
                        <StarIcon />
                    </ListItemIcon>
                    <ListItemText primary={focusedItem.isStarred ? "Remove Star" : "Add Star"} />
                </MenuItem>
                <MenuItem 
                    onClick={() => setShowDeleteDialog(true) }
                    disabled={ disabled }
                    >
                    <ListItemIcon>
                        <DeleteIcon />
                    </ListItemIcon>
                    <ListItemText primary="Delete" />
                </MenuItem>
                {focusedItem.type === LibraryItemType.FileAsset
                    &&
                    <MenuItem 
                        onClick={handleDownload}
                        disabled={ disabled }
                    >
                        <ListItemIcon>
                            <CloudDownloadIcon />
                        </ListItemIcon>
                        <ListItemText primary="Download" />
                    </MenuItem>
                }
                {focusedItem.type === LibraryItemType.BuilderAsset
                    &&
                    [
                        <MenuItem key="offline" onClick={() => onOfflineEnabledToggle()} disabled={ disabled }>
                        <ListItemIcon>
                                <CloudIcon/>
                            </ListItemIcon>
                            <ListItemText primary={focusedItem.offlineMode ? "Offline Disable" : "Offline Enable" }/>
                        </MenuItem>,
                        <MenuItem key="edit" onClick={(e) => history.push(routes.app.resolve.builderPage(focusedItem.id))} disabled={ disabled }>
                            <ListItemIcon>
                                <DescriptionIcon />
                            </ListItemIcon>
                            <ListItemText primary="Edit" />
                        </MenuItem>
                    ]
                }
                {focusedItem.isTemplate
                    &&
                    [
                        <MenuItem key="Duplicate" onClick={() => handleDuplicateTemplate()} disabled={ disabled }>
                            <ListItemIcon>
                                <FileCopyIcon />
                            </ListItemIcon>
                            <ListItemText primary="Duplicate" />
                        </MenuItem>,
                        <MenuItem key="Use Template" onClick={() => setShowUseTemplateDialog(true)} disabled={ disabled }>
                            <ListItemIcon>
                                <ExitToApp />
                            </ListItemIcon>
                            <ListItemText primary="Use Template" />
                        </MenuItem>
                    ]
                }
                <MenuItem onClick={(e) => handleMoveClick(e)} disabled={ disabled }>
                    <ListItemIcon>
                        <DirectionsIcon />
                    </ListItemIcon>
                    <ListItemText primary="Move to..." />
                </MenuItem>
            </MenuList>
            }   
            <RenameAssetDialog focusedItem={focusedItem} open={showRenameDialog} onClose={props.onClose} />
            <DeleteAssetDialog focusedItem={focusedItem} open={showDeleteDialog} onClose={props.onClose} />
            <UseTemplateDialog focusedItem={focusedItem} open={showUseTemplateDialog} onClose={props.onClose} />
            { isMobile && showDetailDrawer &&
                <Drawer open={showDetailDrawer} anchor="right" onClose={() => setShowDetailDrawer(false)} PaperProps={{style: { width: 320 }}}>
                    <RightDrawer item={focusedItem} />
                </Drawer>
            }
        </>
    );
}